// @ts-nocheck
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {AgGridReact} from '@ag-grid-community/react'
import moment from 'moment'

import '@ag-grid-community/styles/ag-grid.css'
import '@ag-grid-community/styles/ag-theme-alpine.css'
import './valveIssues.css'
import api from './api'


const STATUS_OPTIONS = [
  {value: 'ACTIVE', label: 'Активные'},
  {value: 'REVIEW', label: 'К проверке'},
  {value: 'RESOLVED', label: 'Устранённые'},
  {value: 'ALL', label: 'Все'},
]

const VALVE_SYNC_STORAGE_KEY = 'valve-issues-sync-id'
const isAdmin = () => localStorage.getItem('username') === 'admin'

function PageValveIssues() {
  const gridRef = useRef<any>()
  const [rows, setRows] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState('ACTIVE')
  const [depositFilter, setDepositFilter] = useState('')
  const [dnsFilter, setDnsFilter] = useState('')
  const [selectedIssue, setSelectedIssue] = useState<any>(null)
  const [syncTask, setSyncTask] = useState<any>(null)
  const syncTimer = useRef<any>(null)
  const componentMounted = useRef(true)

  useEffect(() => {
    document.title = 'АСПО.Контроль | Задвижки'
    componentMounted.current = true
    const requestId = localStorage.getItem(VALVE_SYNC_STORAGE_KEY)
    if (requestId && isAdmin()) pollSync(requestId)

    return () => {
      componentMounted.current = false
      if (syncTimer.current) window.clearTimeout(syncTimer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadIssues = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.get('', {
        params: {
          status: statusFilter,
        },
      })
      setRows(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      console.error('Не удалось загрузить задвижки:', error)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => {
    loadIssues()
  }, [loadIssues])

  const syncGraph = async () => {
    try {
      const response = await api.post('sync/')
      startSyncPolling(response.data)
    } catch (error) {
      console.error(error)
      if (error.response?.status === 409 && error.response?.data?.request_id) {
        startSyncPolling(
          error.response.data.task || {
            request_id: error.response.data.request_id,
            status: 'QUEUED',
          },
        )
        return
      }
      setSyncTask({
        status: 'ERROR',
        error: error.response?.data?.detail || 'Не удалось запустить синхронизацию',
      })
    }
  }

  const startSyncPolling = (task) => {
    if (!task?.request_id) return
    localStorage.setItem(VALVE_SYNC_STORAGE_KEY, task.request_id)
    setSyncTask(task)
    pollSync(task.request_id)
  }

  const pollSync = async (requestId) => {
    if (syncTimer.current) window.clearTimeout(syncTimer.current)
    try {
      const response = await api.get('sync-status/', {
        params: {request_id: requestId},
      })
      if (!componentMounted.current) return

      const task = response.data
      setSyncTask(task)
      if (['QUEUED', 'RUNNING'].includes(task.status)) {
        syncTimer.current = window.setTimeout(() => pollSync(requestId), 3000)
        return
      }

      localStorage.removeItem(VALVE_SYNC_STORAGE_KEY)
      if (task.status === 'DONE') await loadIssues()
    } catch (error) {
      console.error(error)
      if (!componentMounted.current) return
      localStorage.removeItem(VALVE_SYNC_STORAGE_KEY)
      setSyncTask({
        request_id: requestId,
        status: 'ERROR',
        error: error.response?.data?.detail || 'Не удалось получить результат синхронизации',
      })
    }
  }

  const syncing = ['QUEUED', 'RUNNING'].includes(syncTask?.status)

  const deposits = useMemo(() => uniqueOptions(rows, 'deposit', 'deposit_name'), [rows])
  const dnsOptions = useMemo(() => (
    uniqueOptions(
      depositFilter ? rows.filter((row) => row.deposit === depositFilter) : rows,
      'dns',
      'dns_name',
    )
  ), [rows, depositFilter])
  const visibleRows = useMemo(() => rows.filter((row) => (
    (!depositFilter || row.deposit === depositFilter)
    && (!dnsFilter || row.dns === dnsFilter)
  )), [rows, depositFilter, dnsFilter])

  const columnDefs = useMemo(() => [
    {field: 'org_name', headerName: 'Подрядчик', rowGroup: true, hide: true},
    {
      field: 'well_name',
      headerName: 'Скважина',
      pinned: 'left',
      width: 105,
      cellRenderer: (params) => {
        if (!params.data) return null
        return (
          <button
            type="button"
            className="valve-well-button"
            onClick={() => openIssue(params.data)}
          >
            {params.data.has_unread_messages && <i className="valve-unread-dot"/>}
            {params.value}
          </button>
        )
      },
    },
    {field: 'deposit_name', headerName: 'Месторождение', width: 145, enableRowGroup: true},
    {field: 'dns_name', headerName: 'ДНС', width: 95, enableRowGroup: true},
    {field: 'bush_name', headerName: 'Куст', width: 90},
    {
      field: 'status_display',
      headerName: 'Статус',
      width: 110,
      cellStyle: ({data}) => data ? statusStyle(data.status) : undefined,
    },
    {
      field: 'detected_date',
      headerName: 'Выявлена',
      width: 105,
      valueFormatter: ({value}) => formatDate(value),
    },
    {
      field: 'last_detected_date',
      headerName: 'Последняя отметка',
      width: 135,
      valueFormatter: ({value}) => formatDate(value),
    },
    {field: 'days_open', headerName: 'Дней', width: 70, type: 'numericColumn'},
    {
      field: 'latest_message',
      headerName: 'Последнее сообщение',
      minWidth: 260,
      flex: 1,
      cellRenderer: (params) => {
        if (!params.data) return null
        return (
          <button type="button" className="valve-message-button" onClick={() => openIssue(params.data)}>
            {params.value || 'Добавить сообщение'}
          </button>
        )
      },
    },
    {
      field: 'latest_message_at',
      headerName: 'Обновлено',
      width: 130,
      valueFormatter: ({value}) => value ? moment(value).format('DD.MM.YYYY HH:mm') : '—',
    },
    {
      field: 'fixed_date',
      headerName: 'Устранена',
      width: 105,
      valueFormatter: ({value}) => formatDate(value),
    },
    {field: 'messages_count', headerName: 'Сообщ.', width: 78, type: 'numericColumn'},
  ], [])

  const openIssue = async (row) => {
    const response = await api.get(`${row.id}/`)
    setRows((current) => current.map((item) => (
      item.id === row.id ? {...item, has_unread_messages: false} : item
    )))
    setSelectedIssue(response.data)
  }

  const counters = useMemo(() => ({
    total: visibleRows.length,
    active: visibleRows.filter((row) => row.status === 'ACTIVE').length,
    review: visibleRows.filter((row) => row.status === 'REVIEW').length,
    resolved: visibleRows.filter((row) => row.status === 'RESOLVED').length,
  }), [visibleRows])

  return (
    <div className="valve-page">
      <header className="valve-header">
        <div>
          <h1>Задвижки</h1>
          <p>Неисправные буферные задвижки и переписка по устранению</p>
        </div>
        <div className="valve-header-actions">
          {isAdmin() && (
            <button type="button" onClick={syncGraph} disabled={syncing}>
              {syncing ? 'Синхронизация...' : 'Синхронизировать с графиком'}
            </button>
          )}
          <button type="button" onClick={loadIssues}>Обновить таблицу</button>
        </div>
      </header>

      {isAdmin() && syncTask && (
        <div className={`valve-sync-status is-${String(syncTask.status || '').toLowerCase()}`}>
          {syncStatusText(syncTask)}
        </div>
      )}

      <div className="valve-toolbar">
        <div className="valve-filters">
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            {STATUS_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
          <select
            value={depositFilter}
            onChange={(event) => {
              setDepositFilter(event.target.value)
              setDnsFilter('')
            }}
          >
            <option value="">Все месторождения</option>
            {deposits.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <select value={dnsFilter} onChange={(event) => setDnsFilter(event.target.value)}>
            <option value="">Все ДНС</option>
            {dnsOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </div>
        <div className="valve-counters">
          <span>Показано: <b>{counters.total}</b></span>
          <span>Активных: <b>{counters.active}</b></span>
          <span>К проверке: <b>{counters.review}</b></span>
          <span>Устранено: <b>{counters.resolved}</b></span>
        </div>
      </div>

      <div className="ag-theme-alpine valve-grid">
        <AgGridReact
          ref={gridRef}
          rowData={visibleRows}
          columnDefs={columnDefs}
          loadingOverlayComponentParams={{loadingMessage: 'Загрузка...'}}
          defaultColDef={{sortable: true, resizable: true, filter: true}}
          autoGroupColumnDef={{headerName: 'Подрядчик', pinned: 'left', minWidth: 230}}
          groupDisplayType="singleColumn"
          rowGroupPanelShow="always"
          groupDefaultExpanded={0}
          overlayNoRowsTemplate={loading ? 'Загружаем...' : 'Записей нет'}
          getRowId={(params) => params.data.id}
        />
      </div>

      {selectedIssue && (
        <ValveIssueModal
          issue={selectedIssue}
          onClose={() => setSelectedIssue(null)}
          onUpdated={async () => {
            const response = await api.get(`${selectedIssue.id}/`)
            setSelectedIssue(response.data)
            await loadIssues()
          }}
        />
      )}
    </div>
  )
}

function ValveIssueModal({issue, onClose, onUpdated}) {
  const [text, setText] = useState('')
  const [statusValue, setStatusValue] = useState(issue.status)
  const [fixedDate, setFixedDate] = useState(issue.fixed_date || '')
  const [saving, setSaving] = useState(false)

  const sendMessage = async () => {
    if (!text.trim()) return
    setSaving(true)
    try {
      await api.post(`${issue.id}/messages/`, {text: text.trim()})
      setText('')
      await onUpdated()
    } finally {
      setSaving(false)
    }
  }

  const saveStatus = async () => {
    setSaving(true)
    try {
      await api.patch(`${issue.id}/manage/`, {
        status: statusValue,
        fixed_date: statusValue === 'RESOLVED' ? fixedDate : null,
      })
      await onUpdated()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="valve-modal-overlay" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <div className="valve-modal">
        <div className="valve-modal-header">
          <div>
            <h2>Скважина {issue.well_name}</h2>
            <p>{issue.deposit_name} · {issue.dns_name || 'ДНС не указана'} · куст {issue.bush_name}</p>
          </div>
          <button type="button" className="valve-close" onClick={onClose}>×</button>
        </div>

        <div className="valve-issue-summary">
          <span>Подрядчик <b>{issue.org_name || '—'}</b></span>
          <span>Выявлена <b>{formatDate(issue.detected_date)}</b></span>
          <span>Дней <b>{issue.days_open}</b></span>
          <span>Статус <b>{issue.status_display}</b></span>
        </div>

        {isAdmin() && (
          <div className="valve-manage">
            <label>
              <span>Статус</span>
              <select value={statusValue} onChange={(event) => setStatusValue(event.target.value)}>
                <option value="ACTIVE">Активна</option>
                <option value="REVIEW">К проверке</option>
                <option value="RESOLVED">Устранена</option>
              </select>
            </label>
            {statusValue === 'RESOLVED' && (
              <label>
                <span>Дата устранения</span>
                <input type="date" value={fixedDate} onChange={(event) => setFixedDate(event.target.value)}/>
              </label>
            )}
            <button type="button" onClick={saveStatus} disabled={saving}>Сохранить состояние</button>
          </div>
        )}

        <div className="valve-chat">
          {issue.messages?.map((message) => (
            <article key={message.id} className={`valve-chat-message is-${message.message_type.toLowerCase()}`}>
              <div>
                <b>{message.message_type_display}</b>
                <span>{message.author_name}{message.author_org_name ? ` · ${message.author_org_name}` : ''}</span>
                <time>{moment(message.created_at).format('DD.MM.YYYY HH:mm')}</time>
              </div>
              <p>{message.text}</p>
            </article>
          ))}
        </div>

        <div className="valve-compose">
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Написать сообщение"
            rows={3}
          />
          <button type="button" onClick={sendMessage} disabled={saving || !text.trim()}>
            Отправить
          </button>
        </div>
      </div>
    </div>
  )
}

function uniqueOptions(rows, valueField, labelField) {
  const result = new Map()
  rows.forEach((row) => {
    if (row[valueField] && row[labelField]) result.set(row[valueField], row[labelField])
  })
  return Array.from(result, ([value, label]) => ({value, label}))
    .sort((left, right) => left.label.localeCompare(right.label, 'ru'))
}

function formatDate(value) {
  return value ? moment(value).format('DD.MM.YYYY') : '—'
}

function statusStyle(status) {
  if (status === 'RESOLVED') return {background: '#eaf5e8', color: '#2f6b2f', fontWeight: 600}
  if (status === 'REVIEW') return {background: '#fff4cc', color: '#7a5d00', fontWeight: 600}
  return {background: '#fdecec', color: '#9b2c2c', fontWeight: 600}
}

function syncStatusText(task) {
  if (task.status === 'QUEUED') return 'Синхронизация поставлена в очередь'
  if (task.status === 'RUNNING') return 'Идёт анализ графика'
  if (task.status === 'DONE') {
    const result = task.result || {}
    return `Готово: найдено ${result.matched_wells || 0}, создано ${result.created || 0}, обновлено ${result.updated || 0}`
  }
  if (task.status === 'ERROR') return `Ошибка: ${task.error || 'синхронизация не выполнена'}`
  return ''
}

export default PageValveIssues
